import { useEffect, useRef, useState } from "react";
import type { TagCount } from "../types";
import { ConfirmModal } from "./ConfirmModal";

interface Props {
  tags: TagCount[];
  /** Tag ativa no filtro ("" = nenhuma). */
  value: string;
  onChange: (tag: string) => void;
  /** Exclui a tag de todas as conversas do próprio usuário (com confirmação). */
  onDelete: (tag: string) => void;
}

/**
 * Filtro de tags compacto: um único botão "Tags" abre o popover com a lista
 * completa (contagem + exclusão); a tag ativa aparece no rótulo do botão.
 * Substitui a parede de chips, que crescia com o vocabulário e empurrava a
 * lista de conversas pra baixo.
 */
export function TagFilterPopover({ tags, value, onChange, onDelete }: Props) {
  const [open, setOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (tags.length === 0) return null;

  return (
    <div className="tag-popover-wrap" ref={ref}>
      <button
        type="button"
        className={`tag-chip tag-popover-trigger ${value ? "active" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        title={value ? `Filtrando pela tag "${value}"` : "Filtrar por tag"}
      >
        {value ? `Tag: ${value}` : `Tags (${tags.length})`}
        <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d={open ? "M4 10l4-4 4 4" : "M4 6l4 4 4-4"} />
        </svg>
      </button>
      {open && (
        <div className="tag-popover-menu">
          <div className="tag-popover-list">
            {tags.map((t) => (
              <div key={t.tag} className={`tag-popover-item ${value === t.tag ? "active" : ""}`}>
                <button
                  type="button"
                  className="tag-popover-pick"
                  onClick={() => {
                    onChange(value === t.tag ? "" : t.tag);
                    setOpen(false);
                  }}
                  title={`${t.count} conversa(s) com a tag "${t.tag}"`}
                >
                  <span className="tag-popover-name">{t.tag}</span>
                  <span className="tag-chip-count">{t.count}</span>
                </button>
                <button
                  type="button"
                  className="tag-chip-remove"
                  aria-label={`Excluir a tag "${t.tag}"`}
                  title="Excluir esta tag de todas as suas conversas"
                  onClick={() => setTagToDelete(t.tag)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          {value && (
            <button
              type="button"
              className="search-filters-clear"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
            >
              Limpar filtro
            </button>
          )}
        </div>
      )}
      {tagToDelete !== null && (
        <ConfirmModal
          title="Excluir tag"
          message={`Excluir a tag "${tagToDelete}" de todas as suas conversas? As conversas continuam; só a tag é removida. Tags de outras pessoas não são afetadas.`}
          confirmLabel="Excluir tag"
          onConfirm={() => onDelete(tagToDelete)}
          onClose={() => setTagToDelete(null)}
        />
      )}
    </div>
  );
}
